import { Anchor, Button, DescriptionValidator, DropdownSearch, DropdownSelect, DropdownSelectOption, FieldController, NameValidator, RequiredValidator, TextArea, TextField, useMatchScreenWidth } from '@keplerco/core';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { EntitiesCardWidget } from '../../../../../components/cards/entities-card.widget';
import { Entity } from '../../../../../models';
import { useAppActions, useAppState } from '../../../../../overmind';
import { ImportRoleLayout } from './import-role/import-role.layout';
import { useKeplerNavigate } from '../../../../../navigation/guards/use-kepler-navigate';
import { PagePath } from '../../../../../navigation/navigation.enums';
import { IDetailsStepLayoutProps, RegenerateOption } from './details-step.models';
import { CompanySkillListItemResponse } from '../../../../../models/overmind/company-entity';
import { executeTypedAgentRequest } from '../../../../../overmind/actions';
import { StepSkeleton } from '../../../../../components/general/stepper/step/step.skeleton';
import classNames from 'classnames';
import StarsIcon from '../../../../../design/icons/stars.icon';
import { AIGenerateRoleDescriptionRequest, AIGenerateRoleRequest, AIGenerateRoleResponse, AIGenerateSkillRequest } from '../../../../../models/overmind/ai-agents';
import { AIAgentType } from '../../../../../enums/ai-agent-type';
import { MAX_PAGE_SIZE } from '../../../../../library/consts/consts';
import { SkeletonOverlay } from '../../../../../components/general/loading-state/skeleton-overlay';
import { SearchSkillsRequest } from '../../../../../models/overmind/search-params';

export function DetailsStepLayout({ path, seniorities, roleSlug, selectedRole, parentIsLoading, setSelectedRole, completeStep }: IDetailsStepLayoutProps): JSX.Element {
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables, skillLevelConfig } = useAppState();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [countries, setCountries] = useState<Entity[]>([]);
  const [isRegenerating, setIsRegenerating] = useState<boolean>(false);
  const [skills, setSkills] = useState<CompanySkillListItemResponse[]>([]);
  const [hasError, setHasError] = useState<boolean>(false);

  const keplerAIForm = useForm({ mode: 'onBlur', reValidateMode: 'onBlur' });
  const roleForm = useForm({ mode: 'onBlur', reValidateMode: 'onBlur' });

  const isMobile = useMatchScreenWidth('mobile');

  async function regenerateDescription(instructionType: RegenerateOption) {
    const isValid = await roleForm.trigger(['name', 'seniorityLevel', 'description']);
    if (!isValid) return;

    setIsRegenerating(true);

    const roleFormValues = roleForm.getValues();
    const seniority = (seniorities ?? []).find(seniority => seniority.level === parseInt(roleFormValues.seniorityLevel));

    const response = await executeTypedAgentRequest<AIGenerateRoleDescriptionRequest, { description: string }>(
      AIAgentType.RegenerateRoleAgentType,
      {
        name: roleFormValues.name,
        description: roleFormValues.description,
        seniorityLabel: seniority?.label ?? '',
        instructionType,
        companySlug: companyVariables.slug!,
      },
      companyVariables.slug!
    );

    roleForm.setValue('description', response?.description);

    setIsRegenerating(false);
  }

  const regenerateOptions: DropdownSelectOption[] = useMemo(
    () => [
      { value: RegenerateOption.Expand, onClick: () => regenerateDescription(RegenerateOption.Expand) },
      { value: RegenerateOption.Shorten, onClick: () => regenerateDescription(RegenerateOption.Shorten) },
      { value: RegenerateOption.Simplify, onClick: () => regenerateDescription(RegenerateOption.Simplify) },
      { value: RegenerateOption.Improve, onClick: () => regenerateDescription(RegenerateOption.Improve) },
    ],
    []
  );

  useEffect(() => {
    async function getData() {
      const countriesResponse = await actions.getCountries();
      setCountries(countriesResponse?.sort((a, b) => a.name.localeCompare(b.name)) ?? []);
    }

    if (path === PagePath.roleSkillManagementWizardKeplerAI) getData();
  }, [path]);

  useEffect(() => {
    roleForm.reset({
      name: selectedRole?.name,
      description: selectedRole?.description,
      seniorityLevel: selectedRole?.seniorityLevel?.toString(),
    });
  }, [selectedRole, seniorities]);

  async function onSubmitHandler() {
    if (path !== PagePath.roleSkillManagementWizardCustomRole && !selectedRole) return setHasError(true);

    const isValid = await roleForm.trigger(['name', 'seniorityLevel', 'description']);
    if (!isValid) return;

    const roleFormValues = roleForm.getValues();
    if (path === PagePath.roleSkillManagementWizardCustomRole) setSelectedRole({ ...roleFormValues, name: roleFormValues.name, description: roleFormValues.description, seniorityLevel: parseInt(roleFormValues.seniorityLevel) });

    setIsLoading(true);
    const request = {
      companySlug: companyVariables.slug!,
      name: roleFormValues.name,
      description: roleFormValues.description,
      seniorityLevel: parseInt(roleFormValues.seniorityLevel),
    };

    const response = selectedRole?.slug ? await actions.updateCompanyRole({ ...request, entitySlug: selectedRole?.slug }) : await actions.createCompanyRole(request);
    const slug = response ?? selectedRole?.slug;

    if (!slug) {
      // TODO: error message?
      setIsLoading(false);
      return;
    }

    const keplerAIFormValues = keplerAIForm.getValues();
    completeStep({
      roleSlug: slug,
      role: {
        ...keplerAIFormValues,
        name: keplerAIFormValues.name,
        aboutTheRole: keplerAIFormValues.aboutTheRole,
        aboutTheIndustry: keplerAIFormValues.aboutTheIndustry,
        seniorityLabel: keplerAIFormValues.seniorityLabel,
        country: keplerAIFormValues.country,
        companySlug: companyVariables.slug!,
      },
      skills,
    });
    setIsLoading(false);
  }

  if (parentIsLoading || isLoading) return <StepSkeleton />;

  return (
    <div style={{ width: '100%' }}>
      {path === PagePath.roleSkillManagementWizardKeplerAI && !selectedRole && (
        <EntitiesCardWidget title="Kepler AI" description="Fill in the fields below and we'll do the rest.">
          <div className="divider" />
          <form
            id="generateRoleDetails"
            onSubmit={keplerAIForm.handleSubmit(async fieldValues => {
              setIsLoading(true);
              const seniority = (seniorities ?? []).find(seniority => seniority.level === parseInt(fieldValues.seniorityLevel));
              const roleRequest: AIGenerateRoleRequest = {
                name: fieldValues.name,
                aboutTheRole: fieldValues.description,
                aboutTheIndustry: fieldValues.industry,
                seniorityLabel: seniority?.label ?? '',
                country: fieldValues.countryId,
                companySlug: companyVariables.slug!,
              };
              const roleResponse = await executeTypedAgentRequest<AIGenerateRoleRequest, AIGenerateRoleResponse>(AIAgentType.RoleDetailsAgentType, roleRequest, companyVariables.slug!);

              if (!roleResponse) return setIsLoading(false);

              setSelectedRole({
                name: roleResponse.name,
                description: roleResponse.description,
                seniorityLevel: seniorities.find(seniority => seniority.label === roleResponse.seniorityLabel)?.level,
              });

              const skillRequest: AIGenerateSkillRequest = {
                ...roleRequest,
                seniorityLevels: seniorities,
                skillLevelMappings: skillLevelConfig,
              };
              const skillsResponse = await executeTypedAgentRequest<AIGenerateSkillRequest, CompanySkillListItemResponse[]>(AIAgentType.SkillDetailsAgentType, skillRequest, companyVariables.slug!);

              setSkills(skillsResponse ?? []);

              setIsLoading(false);
            })}
          >
            <FieldController
              control={keplerAIForm.control}
              name="name"
              rules={new NameValidator()}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  type="text"
                  label="Role name"
                  responsive
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: !!fieldState.error,
                    message: fieldState.error?.message ?? 'Enter a name',
                  }}
                />
              )}
            />

            <FieldController
              control={keplerAIForm.control}
              name="aboutTheRole"
              rules={new DescriptionValidator()}
              render={({ field, fieldState }) => (
                <TextArea
                  {...field}
                  label="About the role"
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: !!fieldState.error,
                    message: fieldState.error?.message ?? 'Enter a description',
                  }}
                  responsive
                />
              )}
            />

            <FieldController
              control={keplerAIForm.control}
              name="aboutTheIndustry"
              rules={new DescriptionValidator()}
              render={({ field, fieldState }) => (
                <TextArea
                  {...field}
                  label="About the industry"
                  responsive
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: !!fieldState.error,
                    message: fieldState.error?.message ?? 'Enter a description',
                  }}
                />
              )}
            />

            <FieldController
              control={keplerAIForm.control}
              name="seniorityLevel"
              rules={new RequiredValidator('Select a seniority level')}
              render={({ field, fieldState }) => (
                <DropdownSelect
                  {...field}
                  label="Seniority level"
                  options={seniorities.map(seniority => ({
                    label: seniority.label,
                    value: seniority.level.toString(),
                  }))}
                  responsive
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: !!fieldState.error,
                    message: fieldState.error?.message ?? 'Select a seniority level',
                  }}
                />
              )}
            />

            <FieldController
              control={keplerAIForm.control}
              name="country"
              rules={new RequiredValidator()}
              render={({ field, fieldState }) => (
                <DropdownSearch
                  {...field}
                  label="Country"
                  options={countries?.map(country => ({
                    value: country.name,
                    label: country.name,
                  }))}
                  responsive
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    // invalid: !!fieldState.error, // TODO: why isn't fieldState updating?
                    invalid: !!fieldState.error && !field.value, // TODO: why isn't fieldState updating?
                    message: fieldState.error?.message ?? 'Select a country',
                  }}
                />
              )}
            />

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
              <Button grow={isMobile} type="button" arrow={false} filled>
                <span style={{ alignItems: 'center', display: 'flex', gap: 5, justifyContent: 'center' }}>
                  <StarsIcon /> Generate With AI
                </span>
              </Button>
            </div>
          </form>

          <div className={classNames('formErrorMessage', { invisible: !hasError })} style={{ marginTop: 12 }}>
            Please generate a role to continue
          </div>
        </EntitiesCardWidget>
      )}

      {path === PagePath.roleSkillManagementWizardImportRole && !selectedRole && !roleSlug && (
        <ImportRoleLayout
          selectedRole={selectedRole}
          error={hasError}
          setSelectedRole={async role => {
            setSelectedRole(role);
            if (!role?.slug) return;

            const request: SearchSkillsRequest = {
              search: undefined,
              sortAscending: true,
              pageSize: MAX_PAGE_SIZE,
              page: 1,
              companySlug: companyVariables.slug,
              searchGlobally: true,
              providerName: 'ESCO',
              roleSlugs: [role.slug],
            };
            const response = await actions.getCompanySkills(request);
            setSkills(response?.skills ?? []);
          }}
        />
      )}

      {(path === PagePath.roleSkillManagementWizardCustomRole || !!selectedRole || !!roleSlug) && (
        <EntitiesCardWidget title={path === PagePath.roleSkillManagementWizardKeplerAI ? 'Kepler AI role details' : 'Role details'} description={path === PagePath.roleSkillManagementWizardKeplerAI ? 'The role name and description have been generated for you.' : 'Fill in the fields below'}>
          <form id="saveRoleDetails" onSubmit={onSubmitHandler}>
            <FieldController
              control={roleForm.control}
              name="name"
              rules={new NameValidator()}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  type="text"
                  label="Name"
                  responsive
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: !!fieldState.error,
                    message: fieldState.error?.message ?? 'Enter a name',
                  }}
                />
              )}
            />

            <FieldController
              control={roleForm.control}
              name="seniorityLevel"
              rules={new RequiredValidator('Select a seniority')}
              render={({ field, fieldState }) => (
                <DropdownSelect
                  {...field}
                  label="Seniority"
                  options={seniorities.map(seniority => ({
                    label: seniority.label,
                    value: seniority.level.toString(),
                  }))}
                  responsive
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: !!fieldState.error,
                    message: fieldState.error?.message ?? 'Select a seniority',
                  }}
                />
              )}
            />

            <SkeletonOverlay isLoading={isRegenerating}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 200px', gap: 24 }}>
                <FieldController
                  control={roleForm.control}
                  name="description"
                  rules={new DescriptionValidator()}
                  render={({ field, fieldState }) => (
                    <TextArea
                      {...field}
                      label="Description"
                      responsive
                      validation={{
                        dirty: fieldState.isDirty || !!fieldState.error,
                        invalid: !!fieldState.error,
                        message: fieldState.error?.message ?? 'Enter a description',
                      }}
                    />
                  )}
                />

                <DropdownSelect label="Regenerate" options={regenerateOptions} responsive />
              </div>
            </SkeletonOverlay>
          </form>
        </EntitiesCardWidget>
      )}

      <footer className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--background)', marginTop: 24 }}>
        <Anchor arrow reverse onClick={() => keplerNavigate(`${PagePath.roleSkillManagementBase}${PagePath.roleSkillManagementWizard.replace(':companySlug', companyVariables.slug!)}`)}>
          Cancel
        </Anchor>

        <Anchor arrow onClick={onSubmitHandler}>
          Next
        </Anchor>
      </footer>
    </div>
  );
}
